import { useId } from 'react';
import { format } from 'date-fns';
import { Loader2, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TaxonomyItem } from '@/types/api';

import { MultiSelectField } from './MultiSelectField';

interface TaxonomyTypeFieldsValue {
  locationTypeId: string | null;
  subtypeIds: string[];
}

interface TaxonomyTypeFieldsProps {
  value: TaxonomyTypeFieldsValue;
  onChange: (next: TaxonomyTypeFieldsValue) => void;
  locationTypes: TaxonomyItem[];
  subtypes: { id: string; name: string; parentId: string }[];
  isLoadingSubtypes: boolean;
  /**
   * True while a classification run (Classify with AI, or the classification batch) is choosing
   * this location's category, subtypes, styles and features. Disables the type/subtype editor
   * advisorily (the server's conditional write is the real guarantee: a value a person saved is
   * never replaced by the run).
   */
  isGeneratingType?: boolean;
  /** AI confidence (0-1) for the currently-assigned type, when AI-sourced. */
  typeAiConfidence?: number | null;
  /** Starts a classification run. The "Classify with AI" button renders only when this is set. */
  onClassify?: () => void;
  /** True while the request that starts a run is in flight, before the detail reports the run. */
  isStartingClassification?: boolean;
  /** Why the button is unavailable right now, if it is; shown under it and as its tooltip. */
  classifyDisabledReason?: string;
  /**
   * When the last "Classify with AI" run failed for good (ISO), or null (R5); shown while no run
   * starts or runs.
   */
  lastClassificationFailedAt?: string | null;
}

const CLASSIFY_HINT = 'AI chooses the category, subtypes, styles and features. Anything set by hand is kept.';

/** A stored time as the user reads it: d.m.Y H:i:s in the browser's time zone. */
function displayTime(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : format(at, 'dd.MM.yyyy HH:mm:ss');
}

/**
 * v2 flat-taxonomy type fields: a single L1 Location Type select plus a
 * multi-select of L2 subtypes (children of the chosen L1), and the
 * "Classify with AI" button when the caller offers it.
 *
 * Changing the L1 prunes any previously-selected subtypes (they belong to the
 * old L1's child list). The pruning happens synchronously in the change handler;
 * the parent re-fetches the new L1's subtype list via `useSubtypes`.
 */
export function TaxonomyTypeFields({
  value,
  onChange,
  locationTypes,
  subtypes,
  isLoadingSubtypes,
  isGeneratingType,
  typeAiConfidence,
  onClassify,
  isStartingClassification,
  classifyDisabledReason,
  lastClassificationFailedAt,
}: TaxonomyTypeFieldsProps): JSX.Element {
  const l1Types = locationTypes.filter((t) => !t.parentId);

  const handleLocationTypeChange = (raw: string): void => {
    // Ignore the empty string Radix Select can emit transiently.
    if (!raw) return;
    const next = raw === 'none' ? null : raw;
    onChange({ locationTypeId: next, subtypeIds: [] });
  };

  const subtypeOptions = subtypes.map((s) => ({ value: s.id, label: s.name }));

  // Starting: the request is in flight; generating: the detail reports the run (polled).
  const classifyBusy = isGeneratingType === true || isStartingClassification === true;
  const classifyHintId = useId();

  return (
    <div className="space-y-3">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="locationType" className="flex items-center gap-2">
            Location Type
            <span className="text-xs text-destructive">*</span>
            {isGeneratingType && (
              // The detail query polls while this is set, so it clears when the classify job lands.
              <span
                role="status"
                aria-live="polite"
                data-testid="generating-type-indicator"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground"
              >
                <Loader2 className="h-3 w-3 motion-safe:animate-spin" aria-hidden="true" />
                Generating type…
              </span>
            )}
            {!isGeneratingType && typeAiConfidence != null && (
              <span data-testid="type-ai-confidence" className="text-xs text-muted-foreground">
                AI-assigned · confidence {(typeAiConfidence * 100).toFixed(0)}%
              </span>
            )}
          </Label>
          <Select value={value.locationTypeId ?? 'none'} onValueChange={handleLocationTypeChange}>
            <SelectTrigger id="locationType" data-testid="location-type-select" disabled={isGeneratingType}>
              <SelectValue placeholder="Select location type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {l1Types.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <MultiSelectField
          label="Subtypes"
          testId="location-subtype-select"
          options={subtypeOptions}
          value={value.subtypeIds}
          onChange={(ids) => { onChange({ ...value, subtypeIds: ids }); }}
          disabled={!value.locationTypeId || isLoadingSubtypes || isGeneratingType}
          placeholder={value.locationTypeId ? 'Select subtypes' : 'Select a location type first'}
        />
      </div>

      {onClassify && (
        <div className="flex flex-wrap items-center gap-2">
          {/* type="button": this sits inside the enrichment form and must never submit it. */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onClassify}
            disabled={classifyBusy || classifyDisabledReason !== undefined}
            title={classifyDisabledReason ?? CLASSIFY_HINT}
            aria-describedby={classifyHintId}
            data-testid="classify-with-ai-button"
          >
            {classifyBusy ? (
              <Loader2 className="mr-2 h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Classify with AI
          </Button>
          <span id={classifyHintId} className="text-xs text-muted-foreground" data-testid="classify-with-ai-hint">
            {classifyDisabledReason ?? CLASSIFY_HINT}
          </span>
          {lastClassificationFailedAt != null && !classifyBusy && (
            <span role="status" className="text-xs text-destructive" data-testid="classify-failed-notice">
              Last AI classification failed on {displayTime(lastClassificationFailedAt)}. Try again, or set the
              category by hand.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
